import React, { useEffect } from 'react'

import styled from 'styled-components'

import Modal from 'components/Modal'
import ModalButton from 'components/Modal/Button'

import Api from 'services/api'

import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

import BrowserCompatibility from 'helpers/browserCompatibility'

interface DeleteReportModalProps {
  isDisplayed: boolean
  isDeleting: boolean
  setIsDeleting: (val: boolean) => void
  setDeleteModalOpen: (val: boolean) => void
  setReportToDelete: (
    val: { deviceId: string; s3FileName: string } | null
  ) => void
  reportToDelete: { deviceId: string; s3FileName: string } | null
  onDeleteSuccess: (deviceId: string, s3FileName: string) => void
  onTableUpdate: () => void
}

const DeleteReportModal: React.FC<DeleteReportModalProps> = ({
  isDisplayed,
  isDeleting,
  setIsDeleting,
  setDeleteModalOpen,
  setReportToDelete,
  reportToDelete,
  onDeleteSuccess,
  onTableUpdate
}) => {
  const isMobile = BrowserCompatibility.isMobile(navigator.userAgent)
  const handleCancel = () => {
    setDeleteModalOpen(false)
  }

  const handleDelete = async () => {
    if (!reportToDelete) return
    setIsDeleting(true)
    try {
      await Api.SystemReports.delete(reportToDelete.deviceId, reportToDelete.s3FileName)
      onDeleteSuccess(reportToDelete.deviceId, reportToDelete.s3FileName)
      setDeleteModalOpen(false)
      setReportToDelete(null)
    } catch (error) {
      console.error('Error deleting report:', error)
    } finally {
      setIsDeleting(false)
    }
  }

  useEffect(() => {
    if (!isDisplayed && reportToDelete === null) {
      onTableUpdate()
    }
  }, [isDisplayed, reportToDelete, onTableUpdate])

  return (
    <Modal
      isDisplayed={isDisplayed}
      closeCallback={handleCancel}
      dataTestID="delete-message-modal"
      variant="sm"
      showCloseButton={isMobile}
      paperStyles={isMobile ? { height: '35vh', width: '95vw' } : {}}
    >
      <S.ModalTitle>Delete Report</S.ModalTitle>
      <S.ModalContent>
        <S.ModalDescription>
          Are you sure you want to delete this report? This action cannot be
          undone.
        </S.ModalDescription>
        <S.ButtonRow>
          <ModalButton
            onPress={handleCancel}
            decoration="secondary"
            text="Cancel"
          />
          <ModalButton
            onPress={handleDelete}
            text="Delete"
            decoration="positive"
            showSpinner={isDeleting}
          />
        </S.ButtonRow>
      </S.ModalContent>
    </Modal>
  )
}

export default DeleteReportModal

const S = {
  ModalTitle: styled.div`
    color: ${color('featureToursHeading')};
    font-size: 28px;
    font-weight: 600;
    text-transform: capitalize;
  `,
  ModalContent: styled.div`
    font-size: 14px;
    color: ${color('modalContentText')};
    padding: 16px 0;
  `,
  ModalDescription: styled.p`
    font-size: 17px;
    color: ${color('primaryText')};
    line-height: 20px;
  `,
  ButtonRow: styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 2rem;

    @media ${screenSize.small} {
      flex-direction: column-reverse;
      align-items: center;
      button {
        width: 80% !important;
        height: 36px !important;
      }
    }
  `
}
